/**
 * Dialog Cleanup Utilities
 * 
 * Ensures that Radix UI dialog/alert-dialog overlays don't leave the app paralyzed
 * after closing by properly cleaning up lingering overlays and restoring pointer events.
 */

/**
 * Clean up any lingering dialog side-effects that might leave the page in a
 * bad state after a Radix dialog closes.
 *
 * Previously this function also forcibly set `display: none` and
 * `pointer-events: none` on every closed overlay it found. That was fragile
 * for two reasons:
 *  - When a dialog is reopened within ~350ms (e.g. OAuth callback returns
 *    and we immediately open the billing section), the still-pending timer
 *    would hide the freshly reopened dialog's overlay.
 *  - The inline `display: none` persisted on overlay nodes that Radix may
 *    reuse for the next open, producing a dialog with no visible backdrop.
 *
 * We now only restore body-level styles (pointer-events / overflow), which
 * was the real bug this helper was guarding against, and leave overlay DOM
 * nodes alone. Recent Radix versions manage their own `data-state` → style
 * transitions correctly.
 */
export function cleanupDialogOverlays() {
  requestAnimationFrame(() => {
    setTimeout(() => {
      // Always restore body pointer events (Radix sometimes leaves these set).
      document.body.style.pointerEvents = '';

      // Only clear body overflow if no dialog is currently open.
      const openDialogs = document.querySelectorAll(
        '[data-radix-dialog-content][data-state="open"], [data-radix-alert-dialog-content][data-state="open"]',
      );
      if (openDialogs.length === 0) {
        document.body.style.overflow = '';
      }
    }, 350);
  });
}

/**
 * Force remove all dialog overlays (nuclear option for stuck overlays)
 */
export function forceRemoveAllOverlays() {
  const overlays = document.querySelectorAll(
    '[data-radix-dialog-overlay], [data-radix-alert-dialog-overlay]'
  );
  
  overlays.forEach((overlay) => {
    const element = overlay as HTMLElement;
    element.style.pointerEvents = 'none';
    element.style.display = 'none';
  });
  
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
}

/**
 * Hook-friendly cleanup function that can be called in useEffect cleanup
 */
export function createDialogCleanup(onCleanup?: () => void) {
  return () => {
    cleanupDialogOverlays();
    onCleanup?.();
  };
}

